// src/pages/Finance/StorePayouts/StorePayouts.js
import { useState, useEffect } from "react";
import {
  getStorePayoutsList,
  getStorePayoutsHistory,
  manualPayout,
  exportStorePayoutsCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import "./StorePayouts.css";

// MUI icons (optional – for action buttons)
import HistoryIcon from "@mui/icons-material/History";
import PaymentsIcon from "@mui/icons-material/Payments";

const StorePayouts = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [sortBy, setSortBy] = useState("shopName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [historySortBy, setHistorySortBy] = useState("createdAt");
  const [historySortOrder, setHistorySortOrder] = useState("desc");
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualStoreId, setManualStoreId] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch stores on mount
  const fetchStores = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getStorePayoutsList({ page, pageSize: 10, sortBy, sortOrder });
      console.log(res);
      setStores(res.data || []);
      setPagination(res.pagination || { page, limit: 10, total: 0 });
    } catch (err) {
      console.error("Error fetching store payouts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, [sortBy, sortOrder]);

  // Open history modal and fetch history for a store
  const openHistory = async (storeId, page = 1, nextSortBy = historySortBy, nextSortOrder = historySortOrder) => {
    setSelectedStoreId(storeId);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    try {
      const res = await getStorePayoutsHistory({ storeId, page, pageSize: 10, sortBy: nextSortBy, sortOrder: nextSortOrder });
      setHistoryData(res.data || []);
      setHistoryPagination(res.pagination || { page, limit: 10, total: 0 });
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle manual payout submission
  const handleManualPayout = async (storeId, amount) => {
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }
    setManualLoading(true);
    setError("");
    try {
      const res = await manualPayout({ storeId, amount });
      alert(res.message || "Payout processed successfully.");
      // Refresh store list and close modal
      fetchStores();
      setManualAmount("");
      setManualStoreId(null);
      setManualModalOpen(false);
    } catch (err) {
      console.log(err);
      const msg = err.data?.message;
      setError(msg);
    } finally {
      setManualLoading(false);
    }
  };

  // Helper to format currency
  const formatCurrency = (val) => `$${val?.toFixed(2) || "0.00"}`;

  // Helper for status badge class
  const getStatusClass = (status) => {
    const map = {
      completed: "status-completed",
      failed: "status-failed",
      pending: "status-pending",
    };
    return map[status?.toLowerCase()] || "";
  };

  // Find store name for modal title
  const getStoreName = (id) => stores.find((s) => s.id === id)?.shopName || "";

  return (
    <div className="store-payouts">
      {loading ? (
        <Loader />
      ) : (
        <>
          <ListControls sortOptions={[{value:"shopName",label:"Store"},{value:"totalEarned",label:"Total earned"},{value:"owedAmount",label:"Owed amount"},{value:"completedPayouts",label:"Completed payouts"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
          <ExportCsvButton exportRequest={exportStorePayoutsCsv} filters={{ view: "summary", sortBy, sortOrder }} filename="store-payouts.csv" />
          {/* Table */}
          <div className="data_table_over">
            <table className="data_table">
              <thead>
                <tr>
                  <th>Store Name</th>
                  <th>Owner</th>
                  <th>Total Earned</th>
                  <th>Completed Payouts</th>
                  <th>Owed Amount</th>
                  <th>Schedule</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stores.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      style={{ textAlign: "center", padding: "30px 0" }}
                    >
                      No stores found.
                    </td>
                  </tr>
                ) : (
                  stores.map((store) => (
                    <tr key={store.id}>
                      <td>{store.shopName}</td>
                      <td>{store.fullName}</td>
                      <td>{formatCurrency(store.totalEarned)}</td>
                      <td>{formatCurrency(store.completedPayouts)}</td>
                      <td>
                        <strong>{formatCurrency(store.owedAmount)}</strong>
                      </td>
                      <td>{store.payoutSchedule}</td>
                      <td>
                        <button
                          className="btn-outline"
                          onClick={() => openHistory(store.id)}
                          title="View payout history"
                        >
                          <HistoryIcon fontSize="small" />
                        </button>
                        <button
                          className="sbtn-primary"
                          onClick={() => {
                            setManualStoreId(store.id);
                            setManualModalOpen(true);
                            setError("");
                            setManualAmount("");
                          }}
                          title="Process manual payout"
                        >
                          <PaymentsIcon fontSize="small" /> Payout
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination pagedata={pagination} onPageChange={fetchStores} />

          {/* ====== HISTORY MODAL ====== */}
          <Modal
            isOpen={historyModalOpen}
            onClose={() => setHistoryModalOpen(false)}
            title={`Payout History - ${getStoreName(selectedStoreId)}`}
          >
            {historyLoading ? (
              <div className="history-table-wrap">...loading</div>
            ) : (
              <div className="history-table-wrap">
                <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"amount",label:"Amount"},{value:"status",label:"Status"},{value:"vendor.shopName",label:"Store"}]} sortBy={historySortBy} sortOrder={historySortOrder} onSortByChange={(value) => { setHistorySortBy(value); openHistory(selectedStoreId, 1, value, historySortOrder); }} onSortOrderChange={(value) => { setHistorySortOrder(value); openHistory(selectedStoreId, 1, historySortBy, value); }} />
                <table className="data_table">
                  <thead>
                    <tr>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={{ textAlign: "center" }}>
                          No payout history available.
                        </td>
                      </tr>
                    ) : (
                      historyData.map((item) => (
                        <tr key={item.id}>
                          <td>${item.amount}</td>
                          <td>
                            <span
                              className={`status-badge ${getStatusClass(item.status)}`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td>
                            {item.payoutDate
                              ? new Date(item.payoutDate).toLocaleDateString()
                              : "Pending"}
                          </td>
                          <td className="notes-cell">{item.notes || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <Pagination pagedata={historyPagination} onPageChange={(page) => openHistory(selectedStoreId, page)} />
              </div>
            )}
          </Modal>

          {/* ====== MANUAL PAYOUT MODAL ====== */}
          <Modal
            isOpen={manualModalOpen}
            onClose={() => {
              setManualModalOpen(false);
              setError("");
              setManualAmount("");
            }}
            title={`Manual Payout - ${getStoreName(manualStoreId)}`}
          >
            <div className="manual-payout-form">
              <div className="form-group">
                <label htmlFor="payout-amount">Amount ($)</label>
                <input
                  id="payout-amount"
                  type="number"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="e.g. 500.00"
                  min="0.01"
                  step="0.01"
                  autoFocus
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setManualModalOpen(false);
                    setError("");
                    setManualAmount("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() =>
                    handleManualPayout(manualStoreId, parseFloat(manualAmount))
                  }
                  disabled={manualLoading || !manualAmount}
                >
                  {manualLoading ? "Processing..." : "Submit Payout"}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default StorePayouts;
